import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Search,
  Download,
  PackagePlus,
  AlertOctagon,
  TrendingDown
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import PageHeader from '../components/layout/PageHeader';
import GlassmorphicCard from '../components/common/GlassmorphicCard';
import InputField from '../components/common/InputField';
import SelectField from '../components/common/SelectField';
import Button from '../components/common/Button';
import Table from '../components/common/Table';
import Modal from '../components/common/Modal';
import { fetchData, downloadFile } from '../api/api';
import { formatCurrency } from '../utils/currency';
import { formatDate, getCurrentDateISO } from '../utils/date';
import useDebounce from '../utils/debounce';

const LowInventoryPage = () => {
  const { authToken, selectedShopCode } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [threshold, setThreshold] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const exportFormat = 'csv';

  // Quick Restock Modal
  const [isRestockModalOpen, setIsRestockModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [restockQuantity, setRestockQuantity] = useState(10);
  const [restockNotes, setRestockNotes] = useState('');

  const debouncedSearchTerm = useDebounce(searchTerm, 600);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  const fetchLowInventoryProducts = useCallback(async () => {
    if (!authToken || !selectedShopCode) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (threshold) params.append('threshold', threshold);
      params.append('pageSize', pageSize.toString());
      params.append('pageNumber', currentPage.toString());

      const endpoint = `api/pos/v1/product/low-inventory?${params.toString()}`;
      const data = await fetchData(endpoint, 'GET', null, authToken, selectedShopCode);

      setProducts(data.data?.data || []);
      setTotalPages(data.data?.last_page || 1);
      setTotalItems(data.data?.total || 0);
    } catch (err) {
      showError('Failed to fetch low inventory products.');
      setProducts([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [authToken, selectedShopCode, debouncedSearchTerm, threshold, pageSize, currentPage, showError]);

  useEffect(() => {
    fetchLowInventoryProducts();
  }, [fetchLowInventoryProducts]);

  const handleExport = async () => {
    if (!authToken || !selectedShopCode) return;
    setIsExporting(true);
    try {
      const params = {};
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (threshold) params.threshold = threshold;
      params.format = exportFormat;

      let filename = `low_stock_report_${getCurrentDateISO()}.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`;
      await downloadFile('api/pos/v1/product/low-inventory/download', 'GET', params, authToken, selectedShopCode, filename);
      showSuccess(`Low stock report exported as ${exportFormat.toUpperCase()}.`);
    } catch (err) {
      showError('Failed to export low stock report.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleQuickRestock = async () => {
    if (restockQuantity <= 0) {
      showError('Please enter a valid quantity.');
      return;
    }
    try {
      await fetchData(
        `api/pos/v1/product/inventory/${selectedProduct.id}/add`,
        'POST',
        { quantity: Number(restockQuantity), notes: restockNotes || 'Restocked from Low Inventory Alert' },
        authToken,
        selectedShopCode
      );
      showSuccess(`Successfully restocked ${selectedProduct.name} (+${restockQuantity} units).`);
      setIsRestockModalOpen(false);
      fetchLowInventoryProducts();
    } catch (err) {
      showError('Failed to update stock level.');
    }
  };

  const tableHeaders = ['Product Details', 'Category', 'Current Stock', 'Min Threshold', 'Cost Price', 'Selling Price', 'Expiry Date', 'Action'];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Low Stock Items"
        subtitle="Manage inventory items below threshold levels"
        breadcrumbs={[{ label: 'Low Stock' }]}
        action={
          <Button
            onClick={handleExport}
            disabled={isExporting}
            variant="danger"
            size="sm"
            icon={<Download className="w-3.5 h-3.5" />}
          >
            {isExporting ? 'Exporting...' : 'Export List'}
          </Button>
        }
      />

      {/* Filter and Control Bar */}
      <GlassmorphicCard noPadding>
        <div className="p-4 md:p-5 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
            <div className="w-full sm:w-80">
              <InputField
                placeholder="Search product name, brand, barcode..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="w-4 h-4" />}
                className="!mb-0"
              />
            </div>
            <div className="w-full sm:w-44">
              <SelectField
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                options={[
                  { label: 'Threshold: Product-Set', value: '' },
                  { label: 'Threshold: ≤ 5 Units', value: '5' },
                  { label: 'Threshold: ≤ 10 Units', value: '10' },
                  { label: 'Threshold: ≤ 25 Units', value: '25' },
                  { label: 'Threshold: ≤ 50 Units', value: '50' },
                ]}
                className="!mb-0"
              />
            </div>
            <div className="w-full sm:w-36">
              <SelectField
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                options={[
                  { label: '10 per page', value: 10 },
                  { label: '25 per page', value: 25 },
                  { label: '50 per page', value: 50 },
                  { label: '100 per page', value: 100 },
                ]}
                className="!mb-0"
              />
            </div>
          </div>
        </div>
      </GlassmorphicCard>

      {/* Low Stock Table */}
      <Table
        headers={tableHeaders}
        data={products}
        loading={loading}
        emptyMessage="All products are sufficiently stocked."
        renderRow={(product) => {
          const isZeroStock = product.quantity <= 0;

          return (
            <tr
              key={product.id}
              className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors"
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    isZeroStock ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-600' : 'bg-amber-100 dark:bg-amber-950/60 text-amber-600'
                  }`}>
                    {isZeroStock ? <AlertOctagon className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  </div>
                  <div>
                    <span className="font-semibold text-slate-900 dark:text-white text-sm block">
                      {product.name}
                    </span>
                    <span className="text-xs text-slate-400 font-mono">
                      {product.barcode || product.brand || '—'}
                    </span>
                  </div>
                </div>
              </td>

              <td className="px-6 py-4 whitespace-nowrap">
                <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                  {product.categoryName || 'General'}
                </span>
              </td>

              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                    isZeroStock
                      ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/70 dark:text-rose-300 border border-rose-300 dark:border-rose-800'
                      : 'bg-amber-100 text-amber-800 dark:bg-amber-950/70 dark:text-amber-300 border border-amber-300 dark:border-amber-800'
                  }`}
                >
                  {product.quantity} Units Left
                </span>
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs font-medium text-slate-500 dark:text-slate-400">
                {product.threshold} units
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-600 dark:text-slate-300">
                {formatCurrency(product.costPrice)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(product.sellingPrice)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">
                {product.expiry ? formatDate(product.expiry) : '—'}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-right">
                <Button
                  onClick={() => {
                    setSelectedProduct(product);
                    setRestockQuantity(10);
                    setRestockNotes('');
                    setIsRestockModalOpen(true);
                  }}
                  variant="primary"
                  size="sm"
                  icon={<PackagePlus className="w-3.5 h-3.5" />}
                >
                  Restock
                </Button>
              </td>
            </tr>
          );
        }}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Showing <span className="font-semibold text-slate-900 dark:text-white">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
            <span className="font-semibold text-slate-900 dark:text-white">{Math.min(currentPage * pageSize, totalItems)}</span> of{' '}
            <span className="font-semibold text-slate-900 dark:text-white">{totalItems}</span> items
          </p>

          <div className="flex items-center gap-1.5">
            <Button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage <= 1}
              variant="light"
              size="sm"
            >
              Previous
            </Button>
            <Button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages}
              variant="light"
              size="sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Quick Restock Modal */}
      <Modal
        isOpen={isRestockModalOpen}
        onClose={() => setIsRestockModalOpen(false)}
        title={`Restock • ${selectedProduct?.name || ''}`}
      >
        <div className="space-y-4">
          <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200/80 dark:border-slate-700/80 text-xs flex items-center justify-between">
            <span className="text-slate-500">Current Balance:</span>
            <span className="font-bold text-amber-600 dark:text-amber-400">{selectedProduct?.quantity || 0} units</span>
          </div>

          <InputField
            label="Restock Quantity (+)"
            type="number"
            value={restockQuantity}
            onChange={(e) => setRestockQuantity(e.target.value)}
            placeholder="Quantity to add"
            min="1"
            required
            icon={<PackagePlus className="w-4 h-4" />}
          />

          <InputField
            label="Notes (Optional)"
            value={restockNotes}
            onChange={(e) => setRestockNotes(e.target.value)}
            placeholder="Reason or invoice reference"
          />

          <div className="pt-2">
            <Button
              onClick={handleQuickRestock}
              className="w-full py-3"
            >
              Confirm Restock (+{restockQuantity} Units)
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LowInventoryPage;